import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    email: user?.email || "",
    shipping_address: "",
    shipping_city: "",
    shipping_zip: "",
  });
  const [loading, setLoading] = useState(false);

  const shipping = subtotal >= 75 ? 0 : 8;
  const tax = +(subtotal * 0.08).toFixed(2);
  const total = +(subtotal + shipping + tax).toFixed(2);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your basket is empty.");
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ plant_id: i.id, quantity: i.quantity })),
        ...form,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      navigate(`/order/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-32 text-center">
        <h1 className="font-display text-5xl mb-6">Your basket is empty.</h1>
        <button onClick={() => navigate("/shop")} className="overline border-b border-[var(--gu-primary)] pb-1">Return to the shop</button>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-12 pb-24">
      <div className="overline text-[var(--gu-muted)] mb-4">Checkout</div>
      <h1 className="font-display text-5xl lg:text-6xl font-light tracking-tighter">Nearly home.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 mt-16">
        <form onSubmit={submit} className="lg:col-span-3 space-y-8" data-testid="checkout-form">
          <Field label="Full name" value={form.shipping_name} onChange={set("shipping_name")} required testId="ship-name" />
          <Field type="email" label="Email" value={form.email} onChange={set("email")} required testId="ship-email" />
          <Field label="Address" value={form.shipping_address} onChange={set("shipping_address")} required testId="ship-address" />
          <div className="grid grid-cols-2 gap-6">
            <Field label="City" value={form.shipping_city} onChange={set("shipping_city")} required testId="ship-city" />
            <Field label="Zip / Postal" value={form.shipping_zip} onChange={set("shipping_zip")} required testId="ship-zip" />
          </div>

          <div className="border border-[var(--gu-border)] p-6 bg-[var(--gu-surface-2)]">
            <div className="overline mb-2">Payment — mock</div>
            <p className="text-sm text-[var(--gu-muted)]">
              This is a mock checkout for now. No card is charged. Real payment (Stripe) will be
              wired up next. Your order will be recorded and confirmed.
            </p>
          </div>

          <button type="submit" disabled={loading} data-testid="place-order-btn" className="w-full bg-[var(--gu-primary)] text-[var(--gu-bg)] py-4 overline hover:bg-[var(--gu-primary)]/90 transition-colors disabled:opacity-60">
            {loading ? "Placing order…" : `Place order · $${total.toFixed(2)}`}
          </button>
        </form>

        <aside className="lg:col-span-2">
          <div className="border border-[var(--gu-border)] p-6 space-y-4">
            <div className="overline">Your order</div>
            <div className="space-y-3">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm">
                  <span>{i.name} × {i.quantity}</span>
                  <span>${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="pt-4 border-t border-[var(--gu-border)] space-y-2 text-sm">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
              <Row label="Shipping" value={shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`} />
              <Row label="Tax (est.)" value={`$${tax.toFixed(2)}`} />
            </div>
            <div className="pt-4 border-t border-[var(--gu-border)] flex justify-between font-display text-2xl">
              <span>Total</span>
              <span data-testid="order-total">${total.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <label className="block">
      <span className="overline text-[var(--gu-muted)] block mb-2">{label}</span>
      <input {...rest} data-testid={testId} className="w-full bg-transparent border-b border-[var(--gu-border)] focus:border-[var(--gu-primary)] outline-none py-2 text-lg font-body transition-colors" />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[var(--gu-muted)]">
      <span>{label}</span>
      <span className="text-[var(--gu-primary)]">{value}</span>
    </div>
  );
}
